import React, { useMemo, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { PiInfo } from "react-icons/pi";
import { useSelector } from "react-redux";

import Input from "../shared/TailwindComponents/Input";
import Dropdown, { DropdownOption } from "../shared/TailwindComponents/Dropdown";
import { FolderProps } from "./documents/types";
import Button from "../shared/TailwindComponents/Button";
import { useHistory } from "react-router-dom";
import { useAppDispatch, RootState } from "../../store";
import { createFolderAction } from "../../store/actions/FolderAction";
import { ShareUser } from "../shared/ShareAccessManager";
import CollaboratorSelector, { Collaborator } from "../shared/CollaboratorSelector";

type Props = {
  onClose: () => void;
  existingFolders: FolderProps[];
  selectedFolderItem?: any;
  handleCreateFolder: (data: {
    folderId?: string;
    folderName?: string;
  }) => void | Promise<void>;
  employees: ShareUser[];
  initialShared: ShareUser[];
  onRefresh?: (createdFolderId?: string) => void | Promise<void>;
};

const CreateFolderOnly: React.FC<Props> = ({
  onClose,
  existingFolders,
  selectedFolderItem,
  employees,
  initialShared,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string>("");


  const folderOptions = useMemo(() => {
    const options =
      (existingFolders || [])
        .filter((f) => f.FolderType !== "Locked")
        .map((f) => ({
          label:
            (f as any).FolderName ??
            (f as any).name ??
            (f as any).title ??
            String(f.id ?? f.FolderID ?? ""),
          value: String(f.id ?? f.FolderID ?? ""),
        })) || [];

    if (selectedFolderItem && options.length >= 0) {
      options.push({
        label: selectedFolderItem.FolderName,
        value: selectedFolderItem.FolderID,
      });
    }

    return options;
  }, [existingFolders, selectedFolderItem]);

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      FolderId: Yup.string().optional(),
      FolderName: Yup.string()
        .trim()
        .required(t("Folder name is required")),
    });
  }, [t]);

  const formik = useFormik({
    initialValues: {
      FolderId: (selectedFolderItem && selectedFolderItem?.FolderID) || "",
      FolderName: "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        setError(null);

        // Prepare collaborators with access levels
        const validCollaborators = collaborators
          .map(c => ({
            user_id: parseInt(String(c.user_id), 10),
            access: c.access
          }))
          .filter(c => !Number.isNaN(c.user_id));

        const response = await dispatch(
          createFolderAction({
            FolderName: values.FolderName,
            ParentFolderID: values.FolderId || undefined,
            collaborators: validCollaborators.length > 0 ? validCollaborators : undefined,
          })
        );

        const createdFolderId = response?.FolderID || response?.id;

        if (onRefresh && createdFolderId) {
          await onRefresh(createdFolderId);
        }

        onClose();
      } catch (e: any) {
        setError(e?.message || "Error creating folder");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <div className="flex flex-col px-6 py-6 w-[700px] mt-[-20px] gap-6">
      <form onSubmit={formik.handleSubmit}>
        <div className="grid grid-cols-2 gap-4 mb-2">
          <div>
            <Input
              label={t("Folder Name")}
              type="text"
              name="FolderName"
              value={formik.values.FolderName}
              onChange={formik.handleChange}
              error={
                formik.touched.FolderName
                  ? (formik.errors as any).FolderName
                  : undefined
              }
              placeholder={t("e.g., 2025 Cases")}
              className="rounded py-[10px] placeholder-black hover:bg-gray-100 dark:hover:bg-gray-700 bg-white"
            />
          </div>

          <div>
            <div className="flex items-center gap-1 mb-2">
              <label className="block text-sm text-gray-700 dark:text-gray-300">
                {t("Select Parent Folder")}
              </label>
              <span className="text-gray-500 dark:text-gray-400 text-sm">(optional)</span>
              <div
                className="relative inline-flex items-center"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <button
                  type="button"
                  className="p-0.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                  aria-label="Info"
                >
                  <PiInfo className="h-4 w-4" />
                </button>
                {showTooltip && (
                  <div className="absolute left-6 top-1/2 -translate-y-1/2 z-50 w-48 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2 text-xs text-gray-700 dark:text-gray-200 shadow-lg whitespace-normal">
                    {t("You can select a parent folder or create at root level.")}
                    <div className="absolute -left-1 top-1/2 -translate-y-1/2 h-2 w-2 rotate-45 bg-white dark:bg-gray-800 border-l border-b border-gray-200 dark:border-gray-700" />
                  </div>
                )}
              </div>
            </div>
            <Dropdown
              options={folderOptions}
              value={formik.values.FolderId}
              onChange={(val) => {
                formik.setFieldValue("FolderId", val);
                setSelectedFolderId((Array.isArray(val) ? val[0] : val) || "");
              }}
              placeholder={t("Choose a parent folder")}
              error={
                formik.touched.FolderId ? (formik.errors as any).FolderId : undefined
              }
            />
          </div>
        </div>

        {error && (
          <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-600 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        <CollaboratorSelector
          collaborators={collaborators}
          onCollaboratorsChange={setCollaborators}
          initialShared={selectedFolderId ? initialShared : []}
        />

        <div className="flex justify-end px-4 mt-6">
          <Button
            variant="primary"
            size="small"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? t("Creating...") : t("Create Folder")}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateFolderOnly;
